// Customers view — search, filter, paginate, multi-select for bulk attach.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { listCustomers, listTemplates, bulkAttach, ApiError } from '../lib/api';
import type { Template } from '../lib/types';

const PAGE_SIZE = 25;

export function CustomersView({ shopDomain }: { shopDomain: string }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hasDesigns, setHasDesigns] = useState<'any' | 'yes' | 'no'>('any');
  const [showBulkAttach, setShowBulkAttach] = useState(false);

  const customersQuery = useQuery({
    queryKey: ['customers', { q: searchTerm, shop: shopDomain, offset, hasDesigns }],
    queryFn: () =>
      listCustomers({
        q: searchTerm || undefined,
        shop: shopDomain,
        limit: PAGE_SIZE,
        offset,
        has_designs: hasDesigns === 'any' ? undefined : hasDesigns === 'yes',
      }),
  });

  const total = customersQuery.data?.total ?? 0;
  const customers = customersQuery.data?.customers ?? [];

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === customers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(customers.map((c) => c.id)));
    }
  }

  return (
    <div className="view">
      <header className="view-head">
        <div>
          <h1>Customers</h1>
          <p className="sub">{total.toLocaleString()} total</p>
        </div>
      </header>

      <div className="filters">
        <form
          className="search"
          onSubmit={(e) => {
            e.preventDefault();
            setOffset(0);
            setSearchTerm(q.trim());
          }}
        >
          <Search size={16} />
          <input
            placeholder="Search email, name, brokerage…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </form>
        <select
          value={hasDesigns}
          onChange={(e) => {
            setOffset(0);
            setHasDesigns(e.target.value as 'any' | 'yes' | 'no');
          }}
        >
          <option value="any">All</option>
          <option value="yes">With designs</option>
          <option value="no">Without designs</option>
        </select>
        {selected.size > 0 && (
          <button className="btn btn--primary" onClick={() => setShowBulkAttach(true)}>
            <Send size={14} /> Attach to {selected.size}
          </button>
        )}
      </div>

      {customersQuery.isLoading ? (
        <div className="state">Loading…</div>
      ) : customersQuery.isError ? (
        <div className="state error">{(customersQuery.error as Error).message}</div>
      ) : customers.length === 0 ? (
        <div className="state">No customers match.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={selected.size === customers.length && customers.length > 0}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>Email</th>
              <th>Name</th>
              <th>Brokerage</th>
              <th>Shop</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                </td>
                <td>
                  <Link to={`/customer/${c.id}`}>{c.email}</Link>
                </td>
                <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td>{c.brokerage ?? '—'}</td>
                <td>
                  <span className="tag tag--soft">{c.shopify_shop_domain}</span>
                </td>
                <td>{new Date(c.updated_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pager">
        <button
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span>
          {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
        </span>
        <button
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => setOffset(offset + PAGE_SIZE)}
        >
          Next <ChevronRight size={14} />
        </button>
      </div>

      {showBulkAttach && (
        <BulkAttachModal
          customerIds={[...selected]}
          shopDomain={shopDomain}
          onClose={() => setShowBulkAttach(false)}
          onComplete={() => {
            setShowBulkAttach(false);
            setSelected(new Set());
            void qc.invalidateQueries({ queryKey: ['customers'] });
          }}
        />
      )}
    </div>
  );
}

function BulkAttachModal({
  customerIds,
  shopDomain,
  onClose,
  onComplete,
}: {
  customerIds: string[];
  shopDomain: string;
  onClose: () => void;
  onComplete: () => void;
}) {
  const [templateId, setTemplateId] = useState<string>('');
  const [notify, setNotify] = useState(false);
  const [skipExisting, setSkipExisting] = useState(true);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['templates-for-attach', shopDomain],
    queryFn: () => listTemplates({ shop: shopDomain }),
  });

  const attachMut = useMutation({
    mutationFn: () =>
      bulkAttach({
        customer_ids: customerIds,
        template_id: templateId,
        notify,
        skip_if_already_attached: skipExisting,
      }),
    onSuccess: (resp) => {
      setResult(`${resp.succeeded} succeeded, ${resp.failed} failed, ${resp.skipped} skipped.`);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  const templates: Template[] = templatesQuery.data?.templates ?? [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Attach template to {customerIds.length} customer{customerIds.length === 1 ? '' : 's'}</h3>
        {error && <div className="error-banner">{error}</div>}
        {result ? (
          <>
            <div className="result-banner">{result}</div>
            <div className="modal__actions">
              <button className="btn btn--primary" onClick={onComplete}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <label>
              Template
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">— pick one —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
              />
              <span>Skip customers who already have this template</span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              <span>Email each customer about the new design</span>
            </label>
            <div className="modal__actions">
              <button onClick={onClose}>Cancel</button>
              <button
                className="btn btn--primary"
                disabled={!templateId || attachMut.isPending}
                onClick={() => attachMut.mutate()}
              >
                {attachMut.isPending ? 'Attaching…' : 'Attach'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
